import { Avatar, Button, Stack, Typography } from '@mui/material'
import { toast } from 'react-toastify'
import {
  useAcceptTeamProjectInvitationMutation,
  useDeclineTeamProjectInvitationMutation,
  useGetTeamProjectInvitationsQuery,
} from '../../api/teams'
import type { IProjectTeamInvitationResource } from '../../api/types/projects'
import { extractApiErrorMessage } from '../../utils/api'

type Props = {
  teamId: number
}

export const TeamProjectInvitationsSection = ({ teamId }: Props) => {
  const { data: invitations = [], isFetching } = useGetTeamProjectInvitationsQuery(
    { teamId, status: 'pending' },
    { skip: !teamId },
  )
  const [acceptInvitation, acceptState] = useAcceptTeamProjectInvitationMutation()
  const [declineInvitation, declineState] = useDeclineTeamProjectInvitationMutation()

  const submitting = acceptState.isLoading || declineState.isLoading

  const handleAccept = async (invitation: IProjectTeamInvitationResource) => {
    try {
      await acceptInvitation({ teamId, invitationId: invitation.id }).unwrap()
      toast.success('Команда принята в проект')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось принять приглашение')
    }
  }

  const handleDecline = async (invitation: IProjectTeamInvitationResource) => {
    try {
      await declineInvitation({ teamId, invitationId: invitation.id }).unwrap()
      toast.success('Приглашение отклонено')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось отклонить приглашение')
    }
  }

  if (isFetching && invitations.length === 0) {
    return null
  }

  if (invitations.length === 0) {
    return null
  }

  return (
    <Stack gap={1.5}>
      <Typography variant="h4" sx={{ fontSize: 16, fontWeight: 600 }}>
        Приглашения в проекты
      </Typography>
      {invitations.map((invitation) => {
        const projectName = invitation.project?.name ?? `Проект #${invitation.project_id}`
        const inviterName =
          invitation.inviter?.fullname?.trim() ||
          invitation.inviter?.shortname?.trim() ||
          'Пользователь'
        return (
          <Stack
            key={invitation.id}
            gap={1}
            sx={{
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" gap={1.25} alignItems="center">
              <Avatar
                src={invitation.project?.image ?? undefined}
                alt={projectName}
                sx={{ width: 40, height: 40 }}
              />
              <Stack minWidth={0}>
                <Typography variant="black-14-medium" noWrap>
                  {projectName}
                </Typography>
                <Typography variant="black-12-light" color="text.secondary" noWrap>
                  от {inviterName}
                </Typography>
              </Stack>
            </Stack>
            {invitation.message?.trim() && (
              <Typography variant="black-12-light" color="text.secondary">
                {invitation.message}
              </Typography>
            )}
            <Stack direction="row" gap={1}>
              <Button
                variant="follow"
                size="small"
                onClick={() => void handleAccept(invitation)}
                disabled={submitting}
              >
                Принять
              </Button>
              <Button
                variant="danger"
                size="small"
                onClick={() => void handleDecline(invitation)}
                disabled={submitting}
              >
                Отклонить
              </Button>
            </Stack>
          </Stack>
        )
      })}
    </Stack>
  )
}
